import { useQuery } from "react-query";
import {
    AddSupportTicket,
    DeleteSupportTicket,
    GetAllTickets,
    GetSupportTicketById,
    GetSupportTickets,
    UpdateSupportTicket,
    UpdateTicketStatus,
    AssignTicket,
    GetSupportTicketInteractions,
    AddInteraction,
    AcknowledgeSupportTicketResolution,
    SubmitSupportTicketFeedback,
    CreateTicketByAdmin,
    GetTicketDetails,
    UpdateTicketDueDate,
    UpdateTicketPriority,
    UpdateTicketLocation,
    GetSupportDashboard,
    GetSupportTrend,
    GetSupportSettings,
    UpdateSupportSettings,
    ApproveSupportTicket
} from "../services/backend";
import { useMutation, useQueryClient } from "react-query";
import { Notify, NotifyError } from "../utils/functions";


export function useGetSupportTickets(filters = {}) {
    return useQuery(['support-tickets', filters], () => GetSupportTickets(filters), {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        select: (data) => {
            return data?.data || [];
        },
        onError: (error) => {
            NotifyError("Failed to fetch support tickets", error);
            console.error("Error fetching support tickets:", error);
        }
    });
}

export function useGetSupportSettings({ enabled = true } = {}) {
    return useQuery(['support-module-settings'], () => GetSupportSettings(), {
        enabled,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
        select: (data) => {
            return data?.data?.settings || {};
        },
        onError: (error) => {
            NotifyError("Failed to fetch support settings", error);
            console.error("Error fetching support settings:", error);
        }
    });
}

export function useUpdateSupportSettings() {
    const queryClient = useQueryClient();
    return useMutation((data) => UpdateSupportSettings(data), {
        onSuccess: () => {
            Notify("Support settings updated", "success");
            queryClient.invalidateQueries('support-module-settings');
        },
        onError: (error) => {
            NotifyError("Failed to update support settings", error);
            console.error("Error updating support settings:", error);
        }
    });
}

export function useGetSupportTicketById(ticketId, refetchWindow = 5 * 60 * 1000) {
    return useQuery(
        ['support-ticket', ticketId],
        () => GetSupportTicketById(ticketId),
        {
            enabled: !!ticketId,
            staleTime: 0,
            refetchOnWindowFocus: true,
            refetchInterval: refetchWindow,
            select: (data) => {
                return data?.data || {};
            },
            onError: (error) => {
                NotifyError("Failed to fetch support ticket details", error);
                console.error("Error fetching support ticket details:", error);
            }
        }
    );
}

export function useAddSupportTicket() {
    const queryClient = useQueryClient();
    return useMutation(AddSupportTicket, {
        onSuccess: () => {
            Notify("Support ticket added successfully", "success");
            queryClient.invalidateQueries('support-tickets');
            queryClient.invalidateQueries('user-notifications');
            queryClient.invalidateQueries('customer-dashboard-stats');
        },
        onError: (error) => {
            NotifyError("Failed to add support ticket", error);
            console.error("Error adding support ticket:", error);
        }
    });
}

export function useUpdateSupportTicket() {
    const queryClient = useQueryClient();
    return useMutation(({ ticketId, data }) => UpdateSupportTicket(ticketId, data), {
        onSuccess: () => {
            Notify("Support ticket updated successfully", "success");
            queryClient.invalidateQueries('support-tickets');
        },
        onError: (error) => {
            NotifyError("Failed to update support ticket", error);
            console.error("Error updating support ticket:", error);
        }
    });
}

export function useDeleteSupportTicket() {
    const queryClient = useQueryClient();
    return useMutation(DeleteSupportTicket, {
        onSuccess: () => {
            Notify("Support ticket deleted successfully", "success");
            queryClient.invalidateQueries('support-tickets');
        },
        onError: (error) => {
            NotifyError("Failed to delete support ticket", error);
            console.error("Error deleting support ticket:", error);
        }
    });
}

export function useGetAllTickets(filters = {}) {
    return useQuery(['all-tickets'], () => GetAllTickets(filters), {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        select: (data) => {
            return data?.data || [];
        },
        onError: (error) => {
            NotifyError("Failed to fetch tickets", error);
            console.error("Error fetching tickets:", error);
        }
    });
}

export function useUpdateTicketStatus() {
    const queryClient = useQueryClient();
    return useMutation(({ ticketId, data }) => UpdateTicketStatus(ticketId, data), {
        onSuccess: () => {
            Notify("Ticket status updated successfully", "success");
            queryClient.invalidateQueries('all-tickets');
        },
        onError: (error) => {
            NotifyError("Failed to update ticket status", error);
            console.error("Error updating ticket status:", error);
        }
    });
}

export function useAssignTicket() {
    const queryClient = useQueryClient();
    return useMutation(({ ticketId, data }) => AssignTicket(ticketId, data), {
        onSuccess: () => {
            Notify("Ticket assigned successfully", "success");
            queryClient.invalidateQueries('all-tickets');
        },
        onError: (error) => {
            NotifyError("Failed to assign ticket", error);
            console.error("Error assigning ticket:", error);
        }
    });
}

export function useGetSupportTicketInteractions(ticketId) {
    return useQuery(['support-ticket-interactions', ticketId], () => GetSupportTicketInteractions(ticketId), {
        refetchOnWindowFocus: false,
        staleTime: 0, // Always fetch fresh interactions on mount
        select: (data) => {
            return data?.data || [];
        },
        onError: (error) => {
            NotifyError("Failed to fetch ticket status change interactions for user", error);
            console.error("Error fetching ticket status change interactions for user:", error);
        }
    });
}

export const useAddSupportTicketInteraction = () => {
    const queryClient = useQueryClient();
    return useMutation(({ ticketId, data }) => AddInteraction(ticketId, data), {
        onSuccess: (_, variables) => {
            Notify("Ticket interaction added successfully", "success");
            const { ticketId } = variables || {};
            if (ticketId) {
                queryClient.invalidateQueries(['support-ticket', ticketId]);
                queryClient.invalidateQueries(['support-ticket-details', ticketId]);
                queryClient.invalidateQueries(['support-ticket-interactions', ticketId]);
            }
            queryClient.invalidateQueries('all-tickets');
            queryClient.invalidateQueries('support-tickets');
        },
        onError: (error) => {
            NotifyError("Failed to add ticket interaction", error);
            console.error("Error adding ticket interaction:", error);
        }
    });
}

export const useAddSupportTicketResolutionAcknowledgment = () => {
    const queryClient = useQueryClient();
    return useMutation(({ ticketId, data }) => AcknowledgeSupportTicketResolution(ticketId, data), {
        onSuccess: (_, variables) => {
            Notify("Ticket resolution acknowledgment added successfully", "success");
            const { ticketId } = variables || {};
            if (ticketId) {
                queryClient.invalidateQueries(['support-ticket', ticketId]);
                queryClient.invalidateQueries(['support-ticket-details', ticketId]);
                queryClient.invalidateQueries(['support-ticket-interactions', ticketId]);
            }
            queryClient.invalidateQueries('all-tickets');
            queryClient.invalidateQueries('support-tickets');
        },
        onError: (error) => {
            NotifyError("Failed to add ticket resolution acknowledgment", error);
            console.error("Error adding ticket resolution acknowledgment:", error);
        }
    });
}

export const useSubmitSupportTicketFeedback = () => {
    const queryClient = useQueryClient();
    return useMutation(({ ticketId, data }) => SubmitSupportTicketFeedback(ticketId, data), {
        onSuccess: (_, variables) => {
            Notify("Ticket feedback submitted successfully", "success");
            const { ticketId } = variables || {};
            if (ticketId) {
                queryClient.invalidateQueries(['support-ticket', ticketId]);
                queryClient.invalidateQueries(['support-ticket-details', ticketId]);
                queryClient.invalidateQueries(['support-ticket-interactions', ticketId]);
            }
            queryClient.invalidateQueries('all-tickets');
            queryClient.invalidateQueries('support-tickets');
        },
        onError: (error) => {
            NotifyError("Failed to submit ticket feedback", error);
            console.error("Error submitting ticket feedback:", error);
        }
    });
}

export const useApproveSupportTicket = () => {
    const queryClient = useQueryClient();
    return useMutation((ticketId) => ApproveSupportTicket(ticketId), {
        onSuccess: (_, ticketId) => {
            Notify("Ticket approved successfully", "success");
            if (ticketId) {
                queryClient.invalidateQueries(['support-ticket', ticketId]);
                queryClient.invalidateQueries(['support-ticket-details', ticketId]);
                queryClient.invalidateQueries(['support-ticket-interactions', ticketId]);
            }
            queryClient.invalidateQueries('all-tickets');
            queryClient.invalidateQueries('support-tickets');
        },
        onError: (error) => {
            NotifyError("Failed to approve ticket", error);
            console.error("Error approving ticket:", error);
        }
    });
}

export const useCreateTicketByAdmin = () => {
    const queryClient = useQueryClient();
    return useMutation(CreateTicketByAdmin, {
        onSuccess: () => {
            queryClient.invalidateQueries('all-tickets');
            Notify("Ticket created successfully", "success");
        },
        onError: (error) => {
            NotifyError("Failed to create ticket", error);
            console.error("Error creating ticket:", error);
        }
    });
}

export function useGetSupportTicketDetails(ticketId, refetchWindow = 5 * 60 * 1000) {
    return useQuery(['support-ticket-details', ticketId], () => GetTicketDetails(ticketId), {
        enabled: !!ticketId,
        refetchOnWindowFocus: true,
        staleTime: 0,
        refetchInterval: refetchWindow,
        select: (data) => {
            return data?.data || {};
        },
        onError: (error) => {
            NotifyError("Failed to fetch support ticket details", error);
            console.error("Error fetching support ticket details:", error);
        }
    });
}

export function useUpdateTicketDueDate() {
    const queryClient = useQueryClient();
    return useMutation(({ ticketId, data }) => UpdateTicketDueDate(ticketId, data), {
        onSuccess: () => {
            Notify("Ticket due date updated successfully", "success");
            queryClient.invalidateQueries('all-tickets');
        },
        onError: (error) => {
            NotifyError("Failed to update ticket due date", error);
            console.error("Error updating ticket due date:", error);
        }
    });
}

// Always refetches on mount, so opening the Support tab shows current numbers rather
// than whatever was cached last time — these figures drive decisions and a stale one
// is worse than a slow one. Also polls while open and on window focus.
export function useGetSupportDashboard() {
    return useQuery('support-dashboard', GetSupportDashboard, {
        refetchOnMount: 'always',
        staleTime: 0,
        refetchInterval: 5 * 60 * 1000,
        refetchOnWindowFocus: true,
        select: (data) => data?.data,
        onError: (error) => {
            NotifyError("Failed to fetch support dashboard", error);
            console.error("Error fetching support dashboard:", error);
        }
    });
}

export function useGetSupportTrend(params) {
    return useQuery(['support-trend', params], () => GetSupportTrend(params), {
        refetchOnMount: 'always',
        staleTime: 0,
        keepPreviousData: true, // avoids a chart flash while changing a filter
        select: (data) => data?.data,
        onError: (error) => {
            NotifyError("Failed to fetch support trend", error);
            console.error("Error fetching support trend:", error);
        }
    });
}

export function useUpdateTicketLocation() {
    const queryClient = useQueryClient();
    return useMutation(({ ticketId, data }) => UpdateTicketLocation(ticketId, data), {
        onSuccess: (_result, { ticketId }) => {
            Notify("Ticket location updated successfully", "success");
            queryClient.invalidateQueries('all-tickets');
            queryClient.invalidateQueries('support-dashboard');
            queryClient.invalidateQueries(['support-ticket-details', ticketId]);
        },
        onError: (error) => {
            NotifyError("Failed to update ticket location", error);
            console.error("Error updating ticket location:", error);
        }
    });
}

export function useUpdateTicketPriority() {
    const queryClient = useQueryClient();
    return useMutation(({ ticketId, data }) => UpdateTicketPriority(ticketId, data), {
        onSuccess: (_result, { ticketId }) => {
            Notify("Ticket priority updated successfully", "success");
            queryClient.invalidateQueries('all-tickets');
            queryClient.invalidateQueries(['support-ticket-details', ticketId]);
        },
        onError: (error) => {
            NotifyError("Failed to update ticket priority", error);
            console.error("Error updating ticket priority:", error);
        }
    });
}